import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as QRCode from "qrcode";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import "./index.css";

interface LogEntry {
  id?: number;
  type: "bark" | "quiet" | "snapshot" | "system";
  message: string;
  image_data?: string | null;
  timestamp: string;
}

const BARK_DEBOUNCE_MS = 4000;
const QUIET_DURATION_MS = 8000;
const AUDIO_POLL_MS = 120;
const SHARE_TOKEN_STORAGE_KEY = "dog-monitor-share-token";

function createShareToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function getOrCreateShareToken(): string {
  const existing = localStorage.getItem(SHARE_TOKEN_STORAGE_KEY);
  if (existing) return existing;
  const token = createShareToken();
  localStorage.setItem(SHARE_TOKEN_STORAGE_KEY, token);
  return token;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function eventIcon(type: LogEntry["type"]) {
  return { bark: "🔊", quiet: "😴", snapshot: "📸", system: "⚙️" }[type];
}

function eventColor(type: LogEntry["type"]) {
  return {
    bark: "text-red-400",
    quiet: "text-green-400",
    snapshot: "text-blue-400",
    system: "text-muted-foreground",
  }[type];
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return s > 0 && m < 10 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

interface ActivityPeriod {
  type: "quiet" | "barking";
  start: Date;
  end: Date;
  durationMs: number;
}

interface ViewerState {
  events: LogEntry[];
  latestSnapshot: {
    id: number;
    message: string;
    image_data: string;
    timestamp: string;
  } | null;
  serverTime: string;
}

interface AppConfig {
  notifications?: {
    channel: "discord";
    configured: boolean;
    cooldownSeconds: number;
  };
}

function ViewerApp({ token }: { token: string }) {
  const [state, setState] = useState<ViewerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/view/${encodeURIComponent(token)}/state?limit=60`);
      const data = (await res.json().catch(() => ({}))) as ViewerState | { error?: string };
      if (!res.ok || "error" in data) {
        throw new Error("error" in data && data.error ? data.error : "Viewer link is unavailable");
      }
      setState(data as ViewerState);
      setError(null);
      setLastUpdated(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load dog monitor");
    }
  }, [token]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  const latestEvent = state?.events[0];
  const latestSnapshot = state?.latestSnapshot;
  const barkCount = state?.events.filter((e) => e.type === "bark").length ?? 0;
  const isRecentlyBarking =
    latestEvent?.type === "bark" && Date.now() - new Date(latestEvent.timestamp).getTime() < QUIET_DURATION_MS;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-sm px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-none">Dog Monitor</h1>
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {lastUpdated ? `Updated ${lastUpdated}` : "Connecting..."}
            </p>
          </div>
          <button
            onClick={refresh}
            className="shrink-0 px-3 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/70 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-3 sm:p-4 space-y-4">
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
            {error}
          </div>
        )}

        <Card className={isRecentlyBarking ? "border-red-500/50 bg-red-950/10" : "border-green-500/25"}>
          <CardContent className="py-6 flex items-center gap-4">
            <div className={`text-5xl ${isRecentlyBarking ? "animate-bounce" : ""}`}>
              {isRecentlyBarking ? "🐕" : "😴"}
            </div>
            <div className="min-w-0">
              <p className={`text-2xl font-bold ${isRecentlyBarking ? "text-red-400" : "text-green-400"}`}>
                {isRecentlyBarking ? "Barking recently" : "No recent bark"}
              </p>
              <p className="text-sm text-muted-foreground">
                {latestEvent
                  ? `${latestEvent.message} · ${formatTime(latestEvent.timestamp)}`
                  : "Waiting for monitor activity"}
              </p>
            </div>
          </CardContent>
        </Card>

        {latestSnapshot && (
          <Card className="overflow-hidden">
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  Latest Snapshot
                </CardTitle>
                <span className="text-xs text-muted-foreground">{formatTime(latestSnapshot.timestamp)}</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <img
                src={latestSnapshot.image_data}
                alt="Latest dog monitor snapshot"
                className="w-full aspect-video object-cover cursor-zoom-in"
                onClick={() => setExpandedImage(latestSnapshot.image_data)}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold text-red-400">{barkCount}</p>
              <p className="text-xs text-muted-foreground mt-1">Recent barks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <p className="text-3xl font-bold">{state?.events.length ?? "—"}</p>
              <p className="text-xs text-muted-foreground mt-1">Events loaded</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {(state?.events ?? []).length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-10">No events yet</p>
              ) : (
                state!.events.map((entry, i) => (
                  <div key={entry.id ?? `${entry.timestamp}-${i}`} className="flex gap-3 px-4 py-3">
                    <span className="text-base flex-shrink-0 mt-0.5">{eventIcon(entry.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${eventColor(entry.type)}`}>{entry.message}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(entry.timestamp)} · {formatTime(entry.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setExpandedImage(null)}
        >
          <img src={expandedImage} alt="Expanded dog monitor snapshot" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function buildActivityPeriods(log: LogEntry[], isMonitoring: boolean): ActivityPeriod[] {
  const relevant = log
    .filter((e) => ["bark", "quiet", "system"].includes(e.type))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const periods: ActivityPeriod[] = [];
  let periodStart: Date | null = null;
  let periodType: "quiet" | "barking" | null = null;

  const closePeriod = (end: Date) => {
    if (periodStart && periodType) {
      const durationMs = end.getTime() - periodStart.getTime();
      if (durationMs > 5000) periods.push({ type: periodType, start: periodStart, end, durationMs });
    }
  };

  for (const entry of relevant) {
    const ts = new Date(entry.timestamp);
    if (entry.type === "system" && entry.message === "Monitoring started") {
      periodStart = ts;
      periodType = "quiet";
    } else if (entry.type === "bark" && periodType === "quiet") {
      closePeriod(ts);
      periodStart = ts;
      periodType = "barking";
    } else if (entry.type === "quiet" && periodType === "barking") {
      closePeriod(ts);
      periodStart = ts;
      periodType = "quiet";
    } else if (entry.type === "system" && entry.message === "Monitoring stopped") {
      closePeriod(ts);
      periodStart = null;
      periodType = null;
    }
  }

  if (isMonitoring && periodStart && periodType) {
    const now = new Date();
    const durationMs = now.getTime() - periodStart.getTime();
    if (durationMs > 5000) periods.push({ type: periodType, start: periodStart, end: now, durationMs });
  }

  return periods;
}

export function App() {
  if (window.location.pathname.startsWith("/view/")) {
    const token = decodeURIComponent(window.location.pathname.split("/")[2] ?? "");
    return <ViewerApp token={token} />;
  }

  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isBarking, setIsBarking] = useState(false);
  const [volume, setVolume] = useState(0);
  const [threshold, setThreshold] = useState(0.08);
  const [snapshotIntervalMin, setSnapshotIntervalMin] = useState(5);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [lastSnapshot, setLastSnapshot] = useState<string | null>(null);
  const [notificationConfig, setNotificationConfig] = useState<AppConfig["notifications"] | null>(null);
  const [barkCount, setBarkCount] = useState(0);
  const [snapshotCount, setSnapshotCount] = useState(0);
  const [elapsed, setElapsed] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState(getOrCreateShareToken);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const shareUrl = useMemo(() => `${window.location.origin}/view/${shareToken}`, [shareToken]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioBufferRef = useRef<Float32Array | null>(null);
  const audioIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const snapshotTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const quietTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastBarkRef = useRef(0);
  const isBarkingRef = useRef(false);
  const thresholdRef = useRef(threshold);

  useEffect(() => {
    thresholdRef.current = threshold;
  }, [threshold]);

  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json())
      .then((d: AppConfig) => setNotificationConfig(d.notifications ?? null));
    fetch("/api/log?limit=50")
      .then((r) => r.json())
      .then(setLog);
  }, []);

  useEffect(() => {
    if (!shareUrl) {
      setQrDataUrl("");
      return;
    }
    QRCode.toDataURL(shareUrl, { margin: 1, width: 192, errorCorrectionLevel: "M" })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [shareUrl]);

  const addToLog = useCallback(async (entry: Omit<LogEntry, "id">) => {
    fetch("/api/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...entry, share_token: shareToken }),
    }).catch(() => {});
    setLog((prev) => [entry, ...prev.slice(0, 149)]);
  }, [shareToken]);

  const notify = useCallback(async (title: string, message: string, priority = "default") => {
    const res = await fetch("/api/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, message, priority }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      throttled?: boolean;
      retryAfterSeconds?: number;
    };
    if (!res.ok || !data.ok) {
      const detail = data.error ? `: ${data.error}` : "";
      throw new Error(`Notification failed${detail}`);
    }
    return data;
  }, []);

  const sendTestNotification = useCallback(async () => {
    try {
      setError(null);
      setNotificationStatus(null);
      const result = await notify("🔔 Test", "Dog Monitor is connected!", "default");
      setNotificationStatus(
        result.throttled
          ? `Discord cooldown active (${result.retryAfterSeconds ?? 0}s remaining)`
          : "Discord test sent"
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Notification failed";
      setError(message);
      setNotificationStatus(null);
    }
  }, [notify]);

  const copyShareLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareStatus("Viewer link copied");
    } catch {
      setShareStatus("Could not copy link");
    }
  }, [shareUrl]);

  const shareViewerLink = useCallback(async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      await navigator.share({ title: "Dog Monitor", url: shareUrl }).catch(() => {});
    } else {
      await copyShareLink();
    }
  }, [copyShareLink, shareUrl]);

  const resetShareLink = useCallback(async () => {
    const token = createShareToken();
    localStorage.setItem(SHARE_TOKEN_STORAGE_KEY, token);
    setShareToken(token);
    setShareStatus("New viewer link created");
  }, []);

  const takeSnapshot = useCallback(
    async (reason: string) => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.videoWidth === 0) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
      setLastSnapshot(dataUrl);
      setSnapshotCount((c) => c + 1);
      await addToLog({
        type: "snapshot",
        message: reason,
        image_data: dataUrl,
        timestamp: new Date().toISOString(),
      });
    },
    [addToLog]
  );

  const onBark = useCallback(async () => {
    const now = Date.now();
    if (now - lastBarkRef.current < BARK_DEBOUNCE_MS) return;
    lastBarkRef.current = now;

    if (!isBarkingRef.current) {
      isBarkingRef.current = true;
      setIsBarking(true);
      setBarkCount((c) => c + 1);
      await addToLog({
        type: "bark",
        message: "Barking detected",
        timestamp: new Date().toISOString(),
      });
      notify("🐕 Barking!", "Your dog is barking", "high").catch((err) => {
        const message = err instanceof Error ? err.message : "Notification failed";
        setError(message);
      });
      takeSnapshot("Bark detected");
    }

    if (quietTimerRef.current) clearTimeout(quietTimerRef.current);
    quietTimerRef.current = setTimeout(async () => {
      if (isBarkingRef.current) {
        isBarkingRef.current = false;
        setIsBarking(false);
        await addToLog({
          type: "quiet",
          message: "Dog settled down",
          timestamp: new Date().toISOString(),
        });
      }
    }, QUIET_DURATION_MS);
  }, [addToLog, notify, takeSnapshot]);

  const onBarkRef = useRef(onBark);
  useEffect(() => {
    onBarkRef.current = onBark;
  }, [onBark]);

  const startMonitoring = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.15;
      source.connect(analyser);
      analyserRef.current = analyser;
      audioBufferRef.current = new Float32Array(analyser.fftSize);

      audioIntervalRef.current = setInterval(() => {
        const a = analyserRef.current;
        const buf = audioBufferRef.current;
        if (!a || !buf) return;
        a.getFloatTimeDomainData(buf as Float32Array<ArrayBuffer>);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const sample = buf[i] ?? 0;
          sum += sample * sample;
        }
        const rms = Math.sqrt(sum / buf.length);
        setVolume(rms);
        if (rms > thresholdRef.current) onBarkRef.current();
      }, AUDIO_POLL_MS);

      snapshotTimerRef.current = setInterval(
        () => takeSnapshot("Periodic check-in"),
        snapshotIntervalMin * 60 * 1000
      );

      startTimeRef.current = Date.now();
      elapsedTimerRef.current = setInterval(() => {
        const diff = Date.now() - startTimeRef.current;
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setElapsed(`${h ? h + "h " : ""}${m}m ${s}s`);
      }, 1000);

      setBarkCount(0);
      setSnapshotCount(0);
      setIsMonitoring(true);

      const ts = new Date().toISOString();
      await addToLog({ type: "system", message: "Monitoring started", timestamp: ts });
      setTimeout(() => takeSnapshot("Monitoring started"), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Could not access camera/microphone: ${msg}`);
    }
  };

  const stopMonitoring = async () => {
    if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
    if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    if (quietTimerRef.current) clearTimeout(quietTimerRef.current);

    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioCtxRef.current?.close();

    isBarkingRef.current = false;
    setIsMonitoring(false);
    setIsBarking(false);
    setVolume(0);
    setElapsed("");

    await addToLog({
      type: "system",
      message: "Monitoring stopped",
      timestamp: new Date().toISOString(),
    });
  };

  const clearLog = async () => {
    await fetch("/api/log/clear", { method: "DELETE" });
    setLog([]);
    setLastSnapshot(null);
  };

  const volumeBar = Math.min(100, (volume / 0.3) * 100);
  const thresholdBar = Math.min(100, (threshold / 0.3) * 100);

  // elapsed updates every second while monitoring, keeping the open period fresh
  const activityPeriods = useMemo(
    () => buildActivityPeriods(log, isMonitoring),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [log, isMonitoring, elapsed]
  );
  const quietPeriods = activityPeriods.filter((p) => p.type === "quiet");
  const barkingPeriods = activityPeriods.filter((p) => p.type === "barking");
  const totalQuietMs = quietPeriods.reduce((s, p) => s + p.durationMs, 0);
  const totalBarkingMs = barkingPeriods.reduce((s, p) => s + p.durationMs, 0);
  const longestNapMs = quietPeriods.length > 0 ? Math.max(...quietPeriods.map((p) => p.durationMs)) : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Header ── */}
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-2xl">🐕</span>
            <div className="min-w-0">
              <h1 className="text-lg font-bold leading-none">Dog Monitor</h1>
              {elapsed && (
                <p className="text-xs text-muted-foreground leading-none mt-0.5">
                  {elapsed}
                </p>
              )}
            </div>
          </div>

          {isMonitoring && (
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                isBarking
                  ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/40"
                  : "bg-green-500/15 text-green-400 ring-1 ring-green-500/30"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isBarking ? "bg-red-400 animate-pulse" : "bg-green-400"
                }`}
              />
              {isBarking ? "BARKING" : "QUIET"}
            </div>
          )}

          <button
            onClick={isMonitoring ? stopMonitoring : startMonitoring}
            className={`shrink-0 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
              isMonitoring
                ? "bg-destructive/15 text-destructive hover:bg-destructive/25 ring-1 ring-destructive/40"
                : "bg-primary text-primary-foreground hover:bg-primary/85"
            }`}
          >
            {isMonitoring ? "⏹ Stop" : "▶ Start Monitoring"}
          </button>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto mx-4 mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* ── Main grid ── */}
      <main className="max-w-7xl mx-auto p-3 sm:p-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* ── Left column ── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Camera feed */}
          <Card className="overflow-hidden">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                📹 Live Feed
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="relative">
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="w-full aspect-video object-cover bg-muted"
                />
                <canvas ref={canvasRef} className="hidden" />
                {!isMonitoring && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/60 text-muted-foreground text-sm">
                    Camera off
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Last snapshot */}
          {lastSnapshot && (
            <Card className="overflow-hidden">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  📸 Last Snapshot
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <img
                  src={lastSnapshot}
                  alt="Last snapshot"
                  className="w-full aspect-video object-cover cursor-zoom-in"
                  onClick={() => setExpandedImage(lastSnapshot)}
                />
              </CardContent>
            </Card>
          )}

          {/* Settings */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                ⚙️ Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pb-4">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <label className="font-medium">Bark Sensitivity</label>
                  <span className="text-muted-foreground font-mono text-xs">{threshold.toFixed(3)}</span>
                </div>
                <input
                  type="range"
                  min="0.01"
                  max="0.35"
                  step="0.005"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>More sensitive</span>
                  <span>Less sensitive</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <label className="font-medium">Snapshot Every</label>
                  <span className="text-muted-foreground font-mono text-xs">{snapshotIntervalMin} min</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="1"
                  value={snapshotIntervalMin}
                  onChange={(e) => setSnapshotIntervalMin(Number(e.target.value))}
                  disabled={isMonitoring}
                  className="w-full accent-primary cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </div>

              {isMonitoring && (
                <button
                  onClick={() => takeSnapshot("Manual snapshot")}
                  className="w-full px-3 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/70 rounded-lg text-sm font-medium transition-colors"
                >
                  📸 Take Snapshot Now
                </button>
              )}
            </CardContent>
          </Card>

          {/* Remote viewer */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                📱 Remote Viewer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-4">
              {qrDataUrl && (
                <div className="flex flex-col min-[420px]:flex-row gap-3 min-[420px]:items-center">
                  <img
                    src={qrDataUrl}
                    alt="QR code for private dog monitor viewer"
                    className="w-40 h-40 rounded-lg border border-border bg-white p-2 mx-auto min-[420px]:mx-0"
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <p className="text-sm font-medium">Scan before you leave</p>
                    <p className="text-xs text-muted-foreground">
                      This private link opens a read-only view of snapshots and activity.
                    </p>
                    <code className="block text-[11px] font-mono break-all text-muted-foreground bg-muted/60 rounded p-2">
                      {shareUrl}
                    </code>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={shareViewerLink}
                  disabled={!shareUrl}
                  className="px-3 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/70 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Share
                </button>
                <button
                  onClick={copyShareLink}
                  disabled={!shareUrl}
                  className="px-3 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/70 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Copy Link
                </button>
              </div>
              <button
                onClick={resetShareLink}
                className="w-full px-3 py-2 bg-destructive/15 text-destructive hover:bg-destructive/25 rounded-lg text-sm font-medium transition-colors ring-1 ring-destructive/30"
              >
                Reset Private Link
              </button>
              {shareStatus && (
                <p className="text-xs text-green-400" role="status">
                  {shareStatus}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Discord notifications */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                🔔 Discord Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pb-4">
              <div className="p-3 bg-muted/60 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Status:</p>
                <p
                  className={`text-sm font-semibold ${
                    notificationConfig?.configured ? "text-green-400" : "text-yellow-400"
                  }`}
                >
                  {notificationConfig?.configured ? "Discord webhook configured" : "Discord webhook not configured"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Bark alerts post text-only messages to Discord, throttled to one message every{" "}
                {Math.round((notificationConfig?.cooldownSeconds ?? 300) / 60)} minutes.
              </p>
              <button
                onClick={sendTestNotification}
                className="w-full px-3 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/70 rounded-lg text-sm font-medium transition-colors"
              >
                🔔 Send Discord Test
              </button>
              {notificationStatus && (
                <p className="text-xs text-green-400" role="status">
                  {notificationStatus}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Right column ── */}
        <div className="lg:col-span-3 space-y-4">
          {/* Big status */}
          <Card
            className={`transition-all duration-300 ${
              isBarking
                ? "border-red-500/50 bg-red-950/10"
                : isMonitoring
                ? "border-green-500/25"
                : "border-border"
            }`}
          >
            <CardContent className="py-8 flex flex-col items-center justify-center gap-3">
              <div className={`text-7xl ${isBarking ? "animate-bounce" : ""}`}>
                {!isMonitoring ? "🔇" : isBarking ? "🐕" : "😴"}
              </div>
              <p
                className={`text-center text-2xl sm:text-3xl font-bold tracking-tight ${
                  !isMonitoring
                    ? "text-foreground"
                    : isBarking
                    ? "text-red-400"
                    : "text-green-400"
                }`}
              >
                {!isMonitoring
                  ? "Monitoring Stopped"
                  : isBarking
                  ? "Barking!"
                  : "Quiet / Sleeping"}
              </p>
              {!isMonitoring && (
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm text-muted-foreground mt-1 text-center">
                  {barkCount > 0 && (
                    <span className="text-red-400 font-semibold">{barkCount} bark{barkCount !== 1 ? "s" : ""}</span>
                  )}
                  {snapshotCount > 0 && (
                    <span className="text-blue-400 font-semibold">{snapshotCount} snapshot{snapshotCount !== 1 ? "s" : ""}</span>
                  )}
                  {barkCount === 0 && snapshotCount === 0 && (
                    <span>Press <strong className="text-foreground">Start Monitoring</strong> to begin</span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Volume meter — only shown while monitoring */}
          {isMonitoring && (
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    🎙️ Microphone Level
                  </CardTitle>
                  <span className="text-xs text-muted-foreground font-mono">
                    {volumeBar.toFixed(0)}%
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="relative h-5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 bottom-0 w-px bg-yellow-400/80 z-10 pointer-events-none"
                    style={{ left: `${thresholdBar}%` }}
                  />
                  <div
                    className={`h-full rounded-full transition-all duration-75 ${
                      volumeBar >= thresholdBar ? "bg-red-500" : "bg-emerald-500"
                    }`}
                    style={{ width: `${volumeBar}%` }}
                  />
                </div>
                <div className="flex items-center gap-1 mt-1.5" style={{ paddingLeft: `${Math.max(0, thresholdBar - 2)}%` }}>
                  <span className="text-xs text-yellow-400/80">↑ bark threshold</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-1 min-[390px]:grid-cols-3 gap-3">
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-4xl font-bold text-red-400">{barkCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Barks</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-4xl font-bold text-blue-400">{snapshotCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Snapshots</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-xl font-bold">{elapsed || "—"}</p>
                <p className="text-xs text-muted-foreground mt-1">Duration</p>
              </CardContent>
            </Card>
          </div>

          {/* Session summary */}
          {activityPeriods.length > 0 && (
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  📊 Session Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 space-y-4">
                <div className="grid grid-cols-1 min-[390px]:grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-400">{formatDuration(totalQuietMs)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Total quiet</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">
                      {totalBarkingMs > 0 ? formatDuration(totalBarkingMs) : "—"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Total barking</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{longestNapMs > 0 ? formatDuration(longestNapMs) : "—"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Longest nap</p>
                  </div>
                </div>

                <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                  {activityPeriods.map((period, i) => {
                    const isLongestNap = period.type === "quiet" && period.durationMs === longestNapMs && longestNapMs > 0;
                    return (
                      <div
                        key={i}
                        className={`flex flex-wrap items-center gap-2 sm:gap-3 px-3 py-2 text-sm ${
                          period.type === "quiet" ? "bg-green-950/10" : "bg-red-950/10"
                        }`}
                      >
                        <span className="text-base flex-shrink-0">
                          {period.type === "quiet" ? "😴" : "🔊"}
                        </span>
                        <span className="text-muted-foreground font-mono text-xs">
                          {formatTime(period.start.toISOString())}
                          {" – "}
                          {formatTime(period.end.toISOString())}
                        </span>
                        <span
                          className={`ml-auto font-semibold text-xs ${
                            period.type === "quiet" ? "text-green-400" : "text-red-400"
                          }`}
                        >
                          {formatDuration(period.durationMs)}
                          {isLongestNap && (
                            <span className="ml-1.5 text-yellow-400/80 font-normal">★ longest</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Activity log */}
          <Card>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  📋 Activity Log
                </CardTitle>
                <button
                  onClick={clearLog}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-y-auto max-h-[28rem] divide-y divide-border">
                {log.length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-10">
                    No events yet — start monitoring to see activity
                  </p>
                ) : (
                  log.map((entry, i) => (
                    <div
                      key={entry.id ?? `${entry.timestamp}-${i}`}
                      className="flex gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <span className="text-base flex-shrink-0 mt-0.5">{eventIcon(entry.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${eventColor(entry.type)}`}>
                          {entry.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(entry.timestamp)} · {formatTime(entry.timestamp)}
                        </p>
                        {entry.image_data && (
                          <img
                            src={entry.image_data}
                            alt="Snapshot"
                            className="mt-2 rounded w-28 h-[4.2rem] object-cover cursor-zoom-in opacity-80 hover:opacity-100 transition-opacity"
                            onClick={() => setExpandedImage(entry.image_data!)}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* ── Lightbox ── */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Snapshot"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

export default App;
